import { useState, useRef, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { notification } from 'antd';
import { useAuth } from '../auth/Auth';
import Cookies from 'js-cookie';
import { callAgent, getChat, uploadImage, uploadFile, getUserInfo } from '../backendApi/backend';

interface Message {
  sender: 'agent' | 'human' | 'system';
  text: string;
  videoTracked?: boolean;
}

interface ChatResponse {
  messages: Message[];
}

export const useChat = (uuid?: string) => {
  const navigate = useNavigate();
  const auth = useAuth();

  const [input, setInput] = useState<string>("");
  const [selectedImage, setSelectedImage] = useState<File | null>(null);
  const [selectedFile, setSelectedFile] = useState<File | null>(null);
  const [imagePreviewUrl, setImagePreviewUrl] = useState<string | null>(null);
  const [filePreviewInfo, setFilePreviewInfo] = useState<{name: string, size: string, type: string} | null>(null);
  const [showStarterMessages, setShowStarterMessages] = useState<boolean>(true);
  const [isUploading, setIsUploading] = useState<boolean>(false);
  const [messages, setMessages] = useState<Message[]>([]);
  const [, forceUpdate] = useState(0);
  const [toolName, setToolName] = useState<string>("");
  const [toolStatus, setToolStatus] = useState<string>("");
  const [isFocused, setIsFocused] = useState<boolean>(false);
  const [loadingStarterMessageId, setLoadingStarterMessageId] = useState<string | null>(null);
  const [isWaitingForResponse, setIsWaitingForResponse] = useState<boolean>(false);

  const isStreaming = useRef<boolean>(false);
  const isToolRunningRef = useRef<boolean>(false);
  const inputRef = useRef<HTMLDivElement>(null);
  const justCreatedChat = useRef<boolean>(false);

  useEffect(() => {
    if (uuid) {
      if (justCreatedChat.current && messages.length > 0) {
        justCreatedChat.current = false;
        return;
      }
      justCreatedChat.current = false;

      getChat(uuid)
        .then((response: ChatResponse) => {
          setMessages(response.messages);
          forceUpdate(n => n + 1);
        })
        .catch((error) => console.error('Error occurred:', error));
    } else {
      setMessages([]);
      setShowStarterMessages(true);
    }
  }, [uuid]);

  useEffect(() => {
    if (inputRef.current) {
      inputRef.current.style.height = "auto";
      inputRef.current.style.height = `${inputRef.current.scrollHeight}px`;
    }
  }, [input]);

  const handleSend = async () => {
    const result = await auth.tryRefresh();
    if (!result) {
      navigate('/login');
      return;
    }

    if (input.trim() || selectedImage || selectedFile) {
      let message = input;
      const currentSelectedImage = selectedImage;
      const currentSelectedFile = selectedFile;
      
      // Clear input and file states immediately
      setInput('');
      setSelectedImage(null);
      setSelectedFile(null);
      setImagePreviewUrl(null);
      setFilePreviewInfo(null);
      
      if (inputRef.current) {
        inputRef.current.textContent = "";
        inputRef.current.focus();
      }
      
      if (currentSelectedImage) {
        setIsUploading(true);
        try {
          const response = await uploadImage(currentSelectedImage);
          const imageUrl = response.image_url;
          message += ` [IMAGE_URL] ${imageUrl} [/IMAGE_URL]`;
        } catch (error) {
          console.error('Error uploading image:', error);
          notification.error({
            message: 'Failed to Upload Image',
            description: 'Failed to upload image. Please try again.',
          });
          setIsUploading(false);
          return;
        } finally {
          setIsUploading(false);
        }
      }

      if (currentSelectedFile) {
        setIsUploading(true);
        try {
          const response = await uploadFile(currentSelectedFile);
          const name = response.name;
          message += ` [FILE_NAME] ${name} [/FILE_NAME]`;
        } catch (error) {
          console.error('Error uploading file:', error);
          notification.error({
            message: 'Failed to Upload File',
            description: 'Failed to upload file. Please try again.',
          });
          Cookies.remove('azuread_token', { path: '/' });
          setIsUploading(false);
          return;
        } finally {
          setIsUploading(false);
        }
      }

      setMessages([...messages, { sender: 'human', text: message }]);
      setIsWaitingForResponse(true);

      try {
        const response = await callAgent(message, uuid, (chunk) => {
          let processedChunk = chunk;
          
          // Check if the chunk contains the [TOOL] tag
          const toolTagRegex = /\[TOOL\]([\s\S]*?)\[\/TOOL\]/;
          const toolMatch = processedChunk.match(toolTagRegex);
          if (toolMatch) {
            const extractedToolName = toolMatch[1];
            isToolRunningRef.current = true;
            setIsWaitingForResponse(false);
            setToolName(extractedToolName);
            processedChunk = processedChunk.replace(toolTagRegex, "").trim();
          }

          // Check for auth error signals (e.g. expired OneDrive token)
          const authErrorRegex = /\[AUTH_ERROR\]([\s\S]*?)\[\/AUTH_ERROR\]/g;
          if (authErrorRegex.test(processedChunk)) {
            // Clear the stale Azure AD token cookie
            Cookies.remove('azuread_token', { path: '/' });
            notification.warning({
              message: 'Microsoft session expired',
              description: 'Your Microsoft connection has expired. Please reconnect in Settings to access your OneDrive files.',
              duration: 8,
            });
          }
          processedChunk = processedChunk.replace(/\[AUTH_ERROR\][\s\S]*?\[\/AUTH_ERROR\]/g, "").trim();

          // Check if the chunk contains tool status information
          const statusRegex = /\[TOOL_STATUS\]([\s\S]*?)\[\/TOOL_STATUS\]/g;
          let statusMatch;
          let lastStatusMsg = null;
          
          while ((statusMatch = statusRegex.exec(processedChunk)) !== null) {
            try {
              const statusData = JSON.parse(statusMatch[1]);
              if (statusData.status_msg) {
                lastStatusMsg = statusData.status_msg;
              }
            } catch (e) {
              lastStatusMsg = statusMatch[1];
            }
          }
          
          if (lastStatusMsg !== null) {
            setToolStatus(lastStatusMsg);
          }
          
          processedChunk = processedChunk.replace(statusRegex, "").trim();

          if (processedChunk !== "") {
            setIsWaitingForResponse(false);
            setTimeout(() => {
              isStreaming.current = true;
              setMessages((prevMessages) => {
                const lastMessage = prevMessages[prevMessages.length - 1];
                if (lastMessage && lastMessage.sender === 'agent') {
                  return [
                    ...prevMessages.slice(0, -1),
                    { sender: 'agent', text: lastMessage.text + processedChunk }
                  ];
                } else {
                  return [...prevMessages, { sender: 'agent', text: processedChunk }];
                }
              });
            }, 100);
          }
        });

        const chatid = response.chatid;
        if (chatid && uuid == null) {
          justCreatedChat.current = true;
          navigate(`/chat/${chatid}`);
        }
      } catch (error) {
        console.error('Error occurred:', error);
      } finally {
        isStreaming.current = false;
        isToolRunningRef.current = false;
        setIsWaitingForResponse(false);
        setToolName("");
        setToolStatus("");
      }
    }
  };

  const handleStarterMessageClick = async (message: string) => {
    setShowStarterMessages(false);
    
    let humanMessage = message;
    setMessages(prevMessages => [...prevMessages, { sender: 'human', text: humanMessage }]);
    setIsWaitingForResponse(true);
    
    try {
      const result = await auth.tryRefresh();
      if (!result) {
        navigate('/login');
        return;
      }
      
      const response = await callAgent(humanMessage, uuid, (chunk) => {
        // Similar processing logic as in handleSend
        let processedChunk = chunk;
        
        const toolTagRegex = /\[TOOL\]([\s\S]*?)\[\/TOOL\]/;
        const toolMatch = processedChunk.match(toolTagRegex);
        if (toolMatch) {
          const extractedToolName = toolMatch[1];
          isToolRunningRef.current = true;
          setIsWaitingForResponse(false);
          setToolName(extractedToolName);
          processedChunk = processedChunk.replace(toolTagRegex, "").trim();
        }

        // Check for auth error signals (e.g. expired OneDrive token)
        const authErrorRegex = /\[AUTH_ERROR\]([\s\S]*?)\[\/AUTH_ERROR\]/g;
        if (authErrorRegex.test(processedChunk)) {
          Cookies.remove('azuread_token', { path: '/' });
          notification.warning({
            message: 'Microsoft session expired',
            description: 'Your Microsoft connection has expired. Please reconnect in Settings to access your OneDrive files.',
            duration: 8,
          });
        }
        processedChunk = processedChunk.replace(/\[AUTH_ERROR\][\s\S]*?\[\/AUTH_ERROR\]/g, "").trim();

        const statusRegex = /\[TOOL_STATUS\]([\s\S]*?)\[\/TOOL_STATUS\]/g;
        let statusMatch;
        let lastStatusMsg = null;
        
        while ((statusMatch = statusRegex.exec(processedChunk)) !== null) {
          try {
            const statusData = JSON.parse(statusMatch[1]);
            if (statusData.status_msg) {
              lastStatusMsg = statusData.status_msg;
            }
          } catch (e) {
            lastStatusMsg = statusMatch[1];
          }
        }
        
        if (lastStatusMsg !== null) {
          setToolStatus(lastStatusMsg);
        }
        
        processedChunk = processedChunk.replace(statusRegex, "").trim();

        if (processedChunk !== "") {
          setIsWaitingForResponse(false);
          setTimeout(() => {
            isStreaming.current = true;
            setMessages((prevMessages) => {
              const lastMessage = prevMessages[prevMessages.length - 1];
              if (lastMessage && lastMessage.sender === 'agent') {
                return [
                  ...prevMessages.slice(0, -1),
                  { sender: 'agent', text: lastMessage.text + processedChunk }
                ];
              } else {
                return [...prevMessages, { sender: 'agent', text: processedChunk }];
              }
            });
          }, 100);
        }
      });

      const chatid = response.chatid;
      if (chatid && uuid == null) {
        justCreatedChat.current = true;
        navigate(`/chat/${chatid}`);
      }
    } catch (error) {
      console.error('Error occurred:', error);
    } finally {
      isStreaming.current = false;
      isToolRunningRef.current = false;
      setIsWaitingForResponse(false);
      setToolName("");
      setToolStatus("");
      setLoadingStarterMessageId(null);
    }
  };

  const handleKeyPress = (event: React.KeyboardEvent<HTMLDivElement>) => {
    if (event.key === 'Enter' && !event.shiftKey) {
      event.preventDefault();
      if (input !== "" && !isToolRunningRef.current) {
        handleSend();
      }
    }
  };

  const handleInputFocus = () => {
    setIsFocused(true);
    
    setTimeout(() => {
      if (inputRef.current) {
        inputRef.current.scrollIntoView({ 
          behavior: 'smooth', 
          block: 'nearest' 
        });
      }
    }, 10);
  };

  return {
    // State
    input,
    setInput,
    selectedImage,
    setSelectedImage,
    selectedFile,
    setSelectedFile,
    imagePreviewUrl,
    setImagePreviewUrl,
    filePreviewInfo,
    setFilePreviewInfo,
    showStarterMessages,
    isUploading,
    messages,
    toolName,
    toolStatus,
    isFocused,
    setIsFocused,
    isWaitingForResponse,
    loadingStarterMessageId,
    setLoadingStarterMessageId,

    // Refs
    inputRef,
    isToolRunningRef,

    // Handlers
    handleSend,
    handleStarterMessageClick,
    handleKeyPress,
    handleInputFocus,
  };
};
